import { waitForAuthorizer } from '@/auth/useAuthorizer'
import { useCoreStore } from '@/core/store/useCoreStore'
import { logInfo } from '@/logger'
import type { HermesGuardFn } from '@/router/guards/types'
import { refreshFeatureFlagsForBrand } from '@/utils/refreshFeatureFlagsForBrand'
import { useFeatureFlags } from '@/utils/useFeatureFlags'

const GUEST_IMPORT_FLOW = 'guest-import'
const HESTIA_CUSTOMERS_ROUTE = 'brand.apps.customers'
const SETUP_FLOW_ROUTE_NAMES = new Set([
  'brand.setup.flow',
  'brand.setup.flow.step',
  'location.setup.flow.step',
])

export const HESTIA_CUSTOMERS_FEATURE_FLAG = 'hermes.hestiaCustomers'

// Repeatable route params and query keys both arrive as arrays, and a bare
// `?locationId` contributes a null entry — so take the first usable value
// rather than index 0.
const getFirstString = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    return value.find(
      (entry): entry is string => typeof entry === 'string' && entry.length > 0
    )
  }

  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * Routes the legacy `/setup/guest-import` URL into the Hestia Customers async
 * import dialog.
 *
 * The legacy URL stays a live public entry point — bookmarks, owner.com,
 * Salesforce, and search all still point at it — so it is preserved and
 * redirected rather than removed. Running as a guard means the decision lands
 * before SetupFlowView mounts, so eligible users never initialize or see the
 * legacy flow.
 *
 * The reverse direction (Hestia Customers back to legacy when the flag is off)
 * is owned by `checkHestiaCustomersFeatureFlag`.
 * @param to
 */
export const checkLegacyGuestImportRedirect: HermesGuardFn = async (to) => {
  if (typeof to.name !== 'string' || !SETUP_FLOW_ROUTE_NAMES.has(to.name)) {
    return undefined
  }

  if (getFirstString(to.params.flow) !== GUEST_IMPORT_FLOW) {
    return undefined
  }

  const brandId = getFirstString(to.params.brandId)
  if (!brandId) {
    return undefined
  }

  // The setup routes carry no authZ, but `brand.apps.customers` requires
  // `read customer`. Redirecting a user who lacks it would hand them to
  // checkPermissions and bounce them to home; leave them on the legacy flow
  // they can actually use.
  const authorizer = await waitForAuthorizer()
  if (!authorizer?.can('read', 'customer')) {
    return undefined
  }

  const featureFlags = useFeatureFlags()
  await refreshFeatureFlagsForBrand(brandId)

  if (!featureFlags.isEnabled(HESTIA_CUSTOMERS_FEATURE_FLAG)) {
    return undefined
  }
  const locationId =
    getFirstString(to.params.locationId) ?? getFirstString(to.query.locationId)

  // The link names the location to import into, but Customers reads the
  // location from the store, and `ensureLocationIdParam` rewrites a query
  // locationId that disagrees with it. Adopt the link's location so the URL,
  // the store, and the iframe agree — only once the brand is loaded and the
  // location is known to belong to it. On a cold entry there is nothing to
  // disagree with: the store is empty, so the query survives and
  // ContextSwitcher rehydrates from it.
  const coreStore = useCoreStore()
  if (
    locationId &&
    coreStore.activeBrandId === brandId &&
    coreStore.activeLocationId !== locationId &&
    coreStore.activeBrandLocations.some(
      (location) => location.id === locationId
    )
  ) {
    coreStore.setActiveLocationId(locationId)
  }

  logInfo('guest_import.legacy_reroute.succeeded', {
    brandId,
    destination: 'hestia-customers',
    locationId,
    source: 'legacy-guest-import',
  })

  return {
    name: HESTIA_CUSTOMERS_ROUTE,
    params: { brandId },
    query: {
      locationId,
      openImport: 'true',
      source: 'legacy-guest-import',
    },
  }
}
